import { Grid } from "@mui/material";
import {
  DataGrid,
  gridPreferencePanelStateSelector,
  GridPreferencePanelsValue,
  useGridApiRef,
} from "@mui/x-data-grid";
import * as React from "react";
import ReactTableStyles from "@Components/ReactTable/ReactTableStyles.js";
import { CustomColumnToolbar } from "../ReactTable/ReactTable";
import MultiFilterPanel from "./MultiFilterPanel";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Toolbar for the server-paginated grid: the applied-filter chips followed by the shared
 * column / filter / density / export controls.
 *
 * Declared at module scope rather than inline in `slots` so that its identity is stable across
 * renders. An inline arrow gets a fresh identity every render, which makes React unmount and
 * remount the whole toolbar and discard any state it holds — closing the export dialog whenever
 * the grid re-rendered underneath it.
 */
function ServerTableToolbar({
  savedFilterModel,
  tableColumns,
  onRemoveFilter,
  column,
  filters,
  density,
  exports,
  exportedFileName,
  search,
  excelExport,
}) {
  const activeFilters = savedFilterModel?.items ?? [];

  return (
    <>
      {/* Active Filters */}
      {activeFilters.length > 0 && (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {activeFilters.map((item, idx) => {
            const col = tableColumns.find((c) => c.field === item.field);
            const opts = typeof col?.valueOptions === "function" ? col.valueOptions() : col?.valueOptions;

            // Resolve a single value string to its display label
            const resolveLabel = (v) => {
              const match = opts?.find(o => String(o.value ?? o) === String(v));
              return match?.label ?? match ?? v;
            };

            // For isanyof, split the comma-joined value and resolve each part
            let valueDisplay = "";
            if (item.value !== undefined && item.value !== "") {
              if (item.operator === "isanyof" && typeof item.value === "string") {
                const parts = item.value.split(",").map(v => v.trim()).filter(Boolean);
                valueDisplay = parts.map(resolveLabel).join(", ");
              } else {
                valueDisplay = String(resolveLabel(item.value));
              }
            }

            // Resolve operator display label from column's filterOperators
            const opObj = (col?.filterOperators || []).find(o => o.value === item.operator);
            const operatorDisplay = opObj?.label || opObj?.headerName || item.operator;

            return (
              <div key={idx} style={{ display: "inline-flex", alignItems: "center", marginRight: 8, padding: 4, background: "#f5f5f5", borderRadius: 4, border: "1px solid #e0e0e0", fontSize: 12 }}>
                <strong>{col?.headerName || item.field}</strong>
                <span style={{ color: "#1976d2", margin: "0 4px" }}>{operatorDisplay}</span>
                {valueDisplay && <span>{valueDisplay}</span>}
                <span onClick={() => onRemoveFilter(idx)} style={{ cursor: "pointer", marginLeft: 4, color: "red" }}>✕</span>
              </div>
            );
          })}
        </Grid>
      )}
      {CustomColumnToolbar(column, filters, density, exports, exportedFileName, search, excelExport)}
    </>
  );
}

export default function ServerPaginatedTable({
  columns,
  checkboxSelection,
  disableRowSelectionOnClick,
  exportedFileName,
  disableColumnMenu,
  height = "70vh",
  minHeight = "60px",
  id,
  getRowClassName,
  className,
  initialColumnVisibilityModel,
  rowCount,
  rows,
  isLoading,
  fetchPage,
  getRowId,
  initialSortModel,
  rowHeight,
  excelExport = null,
}) {
  const location = useLocation();
  const cachePrefix = location.pathname.replace(/\//g, "_");
  const getCacheKey = (key) => `${cachePrefix}_${key}`;

  const column = true;
  const filters = true;
  const density = true;
  const exports = false;
  const search = false;
  const sizeOptions = [5, 10, 20, 50];

  // ---------- Persisted Pagination ----------
  const [paginationModel, setPaginationModel] = useState(() => {
    const cached = sessionStorage.getItem(getCacheKey("queryOptions"));
    if (cached) {
      const parsed = JSON.parse(cached);
      return {
        page: parsed.page ?? 0,
        pageSize: parsed.size ?? 10,
      };
    }
    return { page: 0, pageSize: 10 };
  });

  // ---------- Persisted Sort Model ----------
  const [sortModel, setSortModel] = useState(() => {
    const cached = sessionStorage.getItem(getCacheKey("queryOptions"));
    if (cached) {
      const parsed = JSON.parse(cached);
      return parsed.sortBy
        ? [{ field: parsed.sortBy, sort: parsed.sortDir }]
        : initialSortModel ?? [];
    }
    return initialSortModel ?? [];
  });

  // ---------- Persisted Filter Model ----------
  const [filterModel, setFilterModel] = useState(() => {
    const savedFilters = sessionStorage.getItem(getCacheKey("filterState"));
    if (savedFilters && savedFilters !== "[]" && savedFilters !== "{}") {
      try {
        return JSON.parse(savedFilters);
      } catch (e) {
        return { items: [] };
      }
    }
    return { items: [] };
  });
  const [savedFilterModel, setSavedFilterModel] = useState(filterModel);

  // ---------- Persisted Column Visibility ----------
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = sessionStorage.getItem(getCacheKey("colVisibility"));
    return saved ? JSON.parse(saved) : initialColumnVisibilityModel || {};
  });

  const apiRef = useGridApiRef();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // ---------- Query Options ----------
  const queryOptions = {
    page: paginationModel.page,
    size: paginationModel.pageSize,
    sortBy: sortModel.length > 0 ? sortModel[0].field : "id",
    sortDir: sortModel.length > 0 ? sortModel[0].sort : "desc",
  };

  // ---------- Excel Export ----------
  // Built from savedFilterModel, not filterModel: filterModel holds the filter panel's unapplied
  // draft, so exporting it would produce a file that disagrees with the rows on screen.
  // savedFilterModel is the same model fetchPage runs with.
  const excelExportProps = excelExport
    ? {
        ...excelExport,
        filters: savedFilterModel?.items ?? [],
        sortBy: queryOptions.sortBy,
        sortDir: queryOptions.sortDir,
        storageKey: getCacheKey("exportColumns"),
      }
    : null;

  // ---------- Fetch Data on Load ----------
  const [isRestored, setIsRestored] = useState(false);
  useEffect(() => {
    const savedFilterState = sessionStorage.getItem(getCacheKey("filterState"));
    if (savedFilterState && savedFilterState !== "[]" && savedFilterState !== "{}") {
      try {
        const parsed = JSON.parse(savedFilterState);
        setFilterModel(parsed);
        setSavedFilterModel(parsed);
        fetchPage(queryOptions, parsed);
      } catch (e) {
        fetchPage(queryOptions, { items: [] });
      }
    } else {
      fetchPage(queryOptions, { items: [] });
    }
    setIsRestored(true);
  }, []);

  // ---------- Fetch on Pagination / Sort Changes ----------
  useEffect(() => {
    if (isRestored) {
      fetchPage(queryOptions, savedFilterModel);
    }
  }, [paginationModel, sortModel, isRestored]);

  // ---------- Persist Query Options ----------
  useEffect(() => {
    sessionStorage.setItem(
      getCacheKey("queryOptions"),
      JSON.stringify(queryOptions)
    );
  }, [paginationModel, sortModel]);

  // ---------- Persist Filters ----------
  useEffect(() => {
    sessionStorage.setItem(getCacheKey("filterState"), JSON.stringify(filterModel));
  }, [filterModel]);

  // ---------- Filter Panel Open State ----------
  useEffect(() => {
    const unsubscribe = apiRef.current.subscribeEvent("stateChange", () => {
      const preferencePanelState = gridPreferencePanelStateSelector(
        apiRef.current.state
      );
      setIsFilterPanelOpen(
        preferencePanelState.open &&
          preferencePanelState.openedPanelValue === GridPreferencePanelsValue.filters
      );
    });
    return () => unsubscribe();
  }, [apiRef]);

  // ---------- Apply Filters Function ----------
  const applyFilters = (modelArg) => {
    const source = modelArg || filterModel;
    const finalFilterModel = { items: source.items || [] };
    console.log("[applyFilters] modelArg=", JSON.stringify(modelArg), "filterModel=", JSON.stringify(filterModel), "finalFilterModel=", JSON.stringify(finalFilterModel));
    sessionStorage.setItem(getCacheKey("filterState"), JSON.stringify(finalFilterModel));
    setSavedFilterModel(finalFilterModel);
    setFilterModel(finalFilterModel);
    fetchPage(queryOptions, finalFilterModel);
    // Note: panel's onClose prop already calls hideFilterPanel; no need to call it here too.
  };

  // ---------- Remove Single Filter ----------
  const removeFilter = (index) => {
    const updatedFilters = savedFilterModel.items.filter((_, i) => i !== index);
    const finalFilterModel = { items: updatedFilters };
    sessionStorage.setItem(getCacheKey("filterState"), JSON.stringify(finalFilterModel));
    setSavedFilterModel(finalFilterModel);
    setFilterModel(finalFilterModel);
    fetchPage(queryOptions, finalFilterModel);
  };

  // ---------- Enter Key to Apply Filters ----------
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") applyFilters();
    };

    if (isFilterPanelOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterPanelOpen, filterModel]);

  // ---------- Column Visibility ----------
  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    sessionStorage.setItem(getCacheKey("colVisibility"), JSON.stringify(newModel));
  };

  return (
    <ReactTableStyles tableHeight={height} minTableHeight={minHeight}>
      <Grid container justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <DataGrid
            apiRef={apiRef}
            sx={{
              "& .MuiDataGrid-virtualScroller": {
                minHeight,
                scrollbarGutter: "stable",
              },
            }}
            rowHeight={rowHeight}
            getRowId={getRowId ? getRowId : (row) => row.id || (id && row[id])}
            className={`${className || ""} tableHeight`}
            getRowClassName={(params) => {
              let className = params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd";
              className += getRowClassName?.(params) ?? "";
              return className;
            }}
            classes={{
              root: "overrideRoot",
              cell: "overrideCell",
              columnHeader: "columnHeaderOverride",
              row: "overrideRow",
              virtualScroller: "overrideVirtualScroller",
            }}
            columns={columns}
            disableColumnMenu={disableColumnMenu}
            rows={rows}
            loading={isLoading}
            slots={{
              toolbar: ServerTableToolbar,
              filterPanel: MultiFilterPanel,
            }}
            slotProps={{
              toolbar: {
                savedFilterModel,
                tableColumns: columns,
                onRemoveFilter: removeFilter,
                column,
                filters,
                density,
                exports,
                exportedFileName,
                search,
                excelExport: excelExportProps,
              },
              filterPanel: {
                columns,
                apiRef,
                applyFiltersCallback: applyFilters,
                onClose: () => apiRef.current?.hideFilterPanel?.(),
              },
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            initialState={{
              columns: { columnVisibilityModel },
            }}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            pageSizeOptions={sizeOptions}
            paginationModel={paginationModel}
            sortModel={sortModel}
            filterModel={filterModel}
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            onPaginationModelChange={setPaginationModel}
            onSortModelChange={setSortModel}
            onFilterModelChange={(newModel) => {
              setFilterModel(newModel);
            }}
            rowCount={rowCount}
          />
        </Grid>
      </Grid>
    </ReactTableStyles>
  );
}